// src/components/Onboarding/canvas/shimmerDrawing.js
// Shimmer animation drawing functions

import { SHIMMER_CONFIG } from './canvasConstants';

/**
 * Draw skeleton-style shimmer animation
 * Creates a smooth diagonal sweep effect commonly used in skeleton loaders
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas context to draw on
 * @param {number} x - X position
 * @param {number} y - Y position  
 * @param {number} width - Width of shimmer area
 * @param {number} height - Height of shimmer area
 * @param {Object} opts - Configuration options
 * @param {number} opts.phase - Animation phase (0-1)
 * @param {number} opts.angleDeg - Sweep angle in degrees
 * @param {string} opts.baseColor - Background color
 * @param {string} opts.shimmerColor - Highlight sweep color
 * @param {number} opts.bandWidth - Width of shimmer band (0-1 proportion)
 * @param {Object} opts.gradientStops - Gradient stop positions
 */
export function drawSkeletonShimmer(ctx, x, y, width, height, opts) {
  const {
    phase,
    angleDeg,
    baseColor,
    shimmerColor,
    bandWidth,
    gradientStops,
  } = opts;
  
  // Convert angle from degrees to radians
  const angleRad = (angleDeg * Math.PI) / 180;
  
  // Draw base color (semi-transparent background tint)
  ctx.fillStyle = baseColor;
  ctx.fillRect(x, y, width, height);
  
  // Calculate diagonal length for proper sweep coverage
  const diagonal = Math.sqrt(width * width + height * height);
  const bandWidthPx = diagonal * bandWidth;
  
  // Calculate sweep position along the diagonal
  // Phase 0 = band fully off-screen left, Phase 1 = band fully off-screen right
  const totalTravel = diagonal + bandWidthPx * 2;
  const sweepProgress = -bandWidthPx + phase * totalTravel;
  
  // Create rotated gradient for diagonal sweep
  ctx.save();
  ctx.translate(x, y);
  
  // Rotate around center of the area
  ctx.translate(width / 2, height / 2);
  ctx.rotate(angleRad);
  ctx.translate(-width / 2, -height / 2);
  
  // Create horizontal gradient (will appear diagonal after rotation)
  const gradient = ctx.createLinearGradient(
    sweepProgress - bandWidthPx / 2, 0,
    sweepProgress + bandWidthPx / 2, 0
  );
  
  // Build gradient with configurable stops for soft edges
  // Extract stop positions (use defaults if not provided)
  const stops = gradientStops || SHIMMER_CONFIG.gradientStops;
  const transparent = 'rgba(255, 255, 255, 0)';
  
  gradient.addColorStop(0, transparent);                    // Start: fully transparent
  gradient.addColorStop(stops.fadeInEnd, shimmerColor);     // Fade in complete
  gradient.addColorStop(stops.solidStart, shimmerColor);    // Solid region start
  gradient.addColorStop(stops.solidEnd, shimmerColor);      // Solid region end
  gradient.addColorStop(stops.fadeOutStart, shimmerColor);  // Fade out begins
  gradient.addColorStop(1, transparent);                    // End: fully transparent
  
  ctx.fillStyle = gradient;
  
  // Draw oversized rect to cover entire area after rotation
  const oversizeAmount = diagonal;
  ctx.fillRect(
    -oversizeAmount / 2,
    -oversizeAmount / 2,
    width + oversizeAmount,
    height + oversizeAmount
  );
  
  ctx.restore();
}
